/**
 * Typed fetch client for the Stonker gateway — paths and payloads mirror
 * workers/gateway/src/publicApi.ts + admin.ts exactly.
 *
 * The gateway returns RAW payloads (no envelope): success bodies are the
 * shapes in ./types.ts, failures are non-2xx with `{ error: string }`.
 *
 * In VITE_MOCK=1 mode every call resolves from clearly-labeled synthetic
 * fixtures (src/api/mock.ts) after a small artificial delay. The UI shows a
 * persistent MOCK DATA banner.
 */

import { MOCK_MODE } from '../config'
import { MOCK_ADMIN_ROUTES, MOCK_GET_ROUTES } from './mock'
import type {
  AdminHealthResponse,
  ApproveResponse,
  AuditResponse,
  ConfigListResponse,
  ConfigPutResponse,
  HealthResponse,
  KillSwitchResponse,
  MethodologyDoc,
  PauseResumeResponse,
  PerformanceResponse,
  PortfolioCurrentResponse,
  PortfolioHistoryResponse,
  PostsResponse,
  HoldingsResponse,
  RealBrokerResponse,
  RebalanceResponse,
  SignalsResponse,
  TradeDetailResponse,
  TradesResponse,
  UniverseResponse,
} from './types'

export class ApiError extends Error {
  readonly status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

const JSON_HEADERS = { 'Content-Type': 'application/json' } as const

const mockDelay = () =>
  new Promise((r) => setTimeout(r, 200 + Math.random() * 250))

async function mockGet<T>(path: string): Promise<T> {
  await mockDelay()
  // Strip the query string; fixtures ignore limit/cursor/status params.
  const bare = path.split('?')[0] ?? path
  const route = MOCK_GET_ROUTES[bare]
  if (!route) throw new ApiError(`MOCK: no fixture for ${bare}`, 404)
  return route() as T
}

async function mockAdmin<T>(key: string, ...args: unknown[]): Promise<T> {
  await mockDelay()
  const route = MOCK_ADMIN_ROUTES[key]
  if (!route) throw new ApiError(`MOCK: no fixture for admin ${key}`, 404)
  return route(...args) as T
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init)
  const text = await res.text()
  let body: unknown
  try {
    body = text ? JSON.parse(text) : null
  } catch {
    throw new ApiError(
      `Gateway returned non-JSON (${res.status}) for ${path}`,
      res.status,
    )
  }
  if (!res.ok) {
    const message =
      body !== null &&
      typeof body === 'object' &&
      'error' in body &&
      typeof (body as { error: unknown }).error === 'string'
        ? (body as { error: string }).error
        : `HTTP ${res.status} for ${path}`
    throw new ApiError(message, res.status)
  }
  return body as T
}

function get<T>(path: string): Promise<T> {
  if (MOCK_MODE) return mockGet<T>(path)
  return request<T>(path)
}

function post<T>(path: string, payload?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: payload === undefined ? undefined : JSON.stringify(payload),
  })
}

function put<T>(path: string, payload: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PUT',
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
  })
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

// ---------------------------------------------------------------------------
// Public API (GET only, KV/edge-cached 30s)
// ---------------------------------------------------------------------------

export const api = {
  health: () => get<HealthResponse>('/api/v1/health'),
  portfolioCurrent: () =>
    get<PortfolioCurrentResponse>('/api/v1/portfolio/current'),
  portfolioHistory: (limit?: number) =>
    get<PortfolioHistoryResponse>(`/api/v1/portfolio/history${qs({ limit })}`),
  performance: () => get<PerformanceResponse>('/api/v1/performance'),
  trades: (opts: { cursor?: string | null; limit?: number; status?: string } = {}) =>
    get<TradesResponse>(
      `/api/v1/trades${qs({ cursor: opts.cursor, limit: opts.limit, status: opts.status })}`,
    ),
  trade: (id: string) => {
    if (MOCK_MODE) return mockAdmin<TradeDetailResponse>('trade', id)
    return get<TradeDetailResponse>(
      `/api/v1/trades/${encodeURIComponent(id)}`,
    )
  },
  universeLatest: () => get<UniverseResponse>('/api/v1/universe/latest'),
  signalsLatest: () => get<SignalsResponse>('/api/v1/signals/latest'),
  posts: (limit?: number) =>
    get<PostsResponse>(`/api/v1/posts${qs({ limit })}`),
  methodology: () => get<MethodologyDoc>('/api/v1/methodology'),
  /** Read-only view of the real StonkBrokers deployment on mainnet. */
  realBroker: () => get<RealBrokerResponse>('/api/v1/real-broker'),
  holdings: () => get<HoldingsResponse>('/api/v1/holdings'),
} as const

// ---------------------------------------------------------------------------
// Admin API (Cloudflare Access-gated by the gateway in production)
// ---------------------------------------------------------------------------

export const adminApi = {
  getConfig: () => get<ConfigListResponse>('/admin/api/config'),
  /** PUT {key, value}; key "engine" is schema-validated server-side. */
  putConfig: (key: string, value: unknown) => {
    if (MOCK_MODE) return mockAdmin<ConfigPutResponse>('putConfig', key, value)
    return put<ConfigPutResponse>('/admin/api/config', { key, value })
  },
  /** Triggers the rebalance workflow via the agent service binding. */
  rebalance: () => {
    if (MOCK_MODE) return mockAdmin<RebalanceResponse>('rebalance')
    return post<RebalanceResponse>('/admin/api/rebalance', {})
  },
  /**
   * Approve (true) or reject (false) a rebalance waiting at the risk gate.
   * The gateway has a single route for both: POST /admin/api/approve.
   */
  approve: (instanceId: string, approve: boolean) => {
    if (MOCK_MODE) return mockAdmin<ApproveResponse>('approve', instanceId, approve)
    return post<ApproveResponse>('/admin/api/approve', { instanceId, approve })
  },
  pause: () => {
    if (MOCK_MODE) return mockAdmin<PauseResumeResponse>('pause')
    return post<PauseResumeResponse>('/admin/api/pause')
  },
  resume: () => {
    if (MOCK_MODE) return mockAdmin<PauseResumeResponse>('resume')
    return post<PauseResumeResponse>('/admin/api/resume')
  },
  /** No body (or {active:true}) ENGAGES; {active:false} releases. */
  killSwitch: (active: boolean) => {
    if (MOCK_MODE) return mockAdmin<KillSwitchResponse>('killSwitch', active)
    return post<KillSwitchResponse>('/admin/api/kill-switch', { active })
  },
  audit: (limit?: number) =>
    get<AuditResponse>(`/admin/api/audit${qs({ limit })}`),
  healthFull: () => get<AdminHealthResponse>('/admin/api/health/full'),
} as const
